import { useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { C } from '@ridgewill/brand';
import { HeaderSlot } from '../App.js';
import { inputStyle } from './ui.js';
import type { ChipSearch } from '../lib/useChips.js';

/**
 * The layout every list screen shares, and the controls that live in it.
 *
 * ── Three bands, in this order ───────────────────────────────────────
 *
 *   ┌─ action row ──────────────────────────────────────────────┐
 *   │  filters / tabs / bulk actions        [2nd] [PRIMARY]     │
 *   ├─ chips ───────────────────────────────────────────────────┤
 *   ├─ table ───────────────────────────────────────────────────┤
 *
 * ── Why the primary action moved to the top ──────────────────────────
 *
 * It was at the bottom: every create action was an inline `<details>` anchored
 * BELOW the list. On Agreements, which returns over a thousand rows on dev,
 * "create an agreement" was a thousand rows down the page — reachable only by
 * scrolling past everything you were not looking for.
 *
 * The instinct behind putting it there was right. An admin surface where
 * "publish to every user" sits in the open next to a table is one where it gets
 * clicked by somebody who meant to sort a column. The answer to that is a modal,
 * which asks for confirmation by construction, not the bottom of the page.
 *
 * So: ONE primary per screen, top right, opening a modal. Secondary actions
 * immediately to its left. Destructive and consequential things are not in this
 * row at all — they live in the row menu or inside the detail modal, next to the
 * specific record they affect.
 */
export function Screen({
  filters,
  actions,
  chips,
  children,
}: {
  /** The left of the action row: filter pills, tabs, or a bulk-action bar. */
  filters?: ReactNode;
  /** The right of it. Primary last, so it sits hard against the edge. */
  actions?: ReactNode;
  chips?: ReactNode;
  children: ReactNode;
}) {
  const slot = useContext(HeaderSlot);

  const bar =
    filters || actions || chips ? (
      <div style={s.headerBands}>
        {filters || actions ? (
          <div style={s.actionRow}>
            <div style={s.actionLeft}>{filters}</div>
            <div style={s.actionRight}>{actions}</div>
          </div>
        ) : null}
        {chips}
      </div>
    ) : null;

  return (
    <>
      {/*
        The bands render in the SHELL'S HEADER, not here (org-admin#7).

        They used to sit above the table, so the thing you were reading started
        a third of the way down the screen and moved every time a chip was
        added. In the header they are chrome: fixed, always in the same place,
        and outside what scrolls.

        The portal is what makes that possible without every screen having to
        hand its controls up to the shell — ownership stays here, only the DOM
        node changes. Rendered inline as a fallback if the slot is somehow
        absent, because controls that vanish are worse than controls in the
        wrong place.
      */}
      {bar ? (slot ? createPortal(bar, slot) : bar) : null}
      <div style={{ display: 'grid', gap: '.9rem' }} className="rw-fade">
        {children}
      </div>
    </>
  );
}

/**
 * The chip search box and the chips it commits.
 *
 * ── Enter commits; nothing happens on keystroke ──────────────────────
 *
 * Which is why there is no debounce here and does not need to be one: a chip is
 * created by a deliberate action, so requests are already bounded by intent
 * rather than by typing speed. The placeholder says so, because an input that
 * looks like a live filter and is not is worse than either.
 *
 * Every change — commit, remove, disable, re-enable — goes through `useChips`
 * and lands in a fetch dependency. Nothing in this component touches rows.
 */
export function ChipBar({
  search,
  placeholder = 'Search, then press Enter to filter…',
  /** What the SERVER says it filtered on. Rendered as a warning when it differs
   *  from what was sent, rather than left for the admin to notice. */
  appliedByServer,
}: {
  search: ChipSearch;
  placeholder?: string;
  appliedByServer?: string[];
}) {
  const [draft, setDraft] = useState('');
  const id = useId();

  const commit = () => {
    // `add` already ignores a blank term; clearing regardless keeps the box
    // empty after Enter whether or not anything was committed.
    search.add(draft);
    setDraft('');
  };

  const dropped =
    appliedByServer && search.active.filter((t) => !appliedByServer.includes(t));

  return (
    <div style={{ display: 'grid', gap: '.5rem' }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          commit();
        }}
        style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}
      >
        <input
          id={id}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          /**
           * Enter handled EXPLICITLY, not left to the form's implicit
           * submission.
           *
           * A form with no submit button submits on Enter only under a rule with
           * conditions — the count of fields that "block implicit submission" —
           * and this form's contents change as chips appear. Measured in a
           * browser: typing a term and pressing Enter added nothing and issued no
           * request, which is the entire interaction failing silently on the one
           * screen whose search is the point.
           *
           * The `onSubmit` stays for the case where a browser does submit, so
           * the chip is committed once either way rather than twice.
           */
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            commit();
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          style={{ ...inputStyle, maxWidth: 340 }}
        />
        {search.chips.length > 0 ? (
          <button type="button" onClick={search.clear} style={s.linkButton}>
            Clear all
          </button>
        ) : null}
      </form>

      {search.chips.length > 0 ? (
        <div style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {search.chips.map((c) => (
            <span key={c.term} style={{ ...s.chip, ...(c.enabled ? {} : s.chipOff) }}>
              {/* The chip body toggles; the × removes. Two targets in one
                  control, which is what the old portal had and what makes
                  "take that one off for a second" cheaper than retyping it. */}
              <button
                type="button"
                onClick={() => search.toggle(c.term)}
                title={c.enabled ? 'Click to disable this term' : 'Click to apply this term again'}
                style={s.chipBody}
              >
                {c.term}
              </button>
              <button
                type="button"
                onClick={() => search.remove(c.term)}
                aria-label={`Remove ${c.term}`}
                style={s.chipX}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {dropped && dropped.length > 0 ? (
        <p style={s.dropped}>
          The server applied {appliedByServer?.length} of these terms and ignored{' '}
          {dropped.map((t) => `“${t}”`).join(', ')}. Results are wider than the chips suggest.
        </p>
      ) : null}
    </div>
  );
}

/**
 * One exclusive choice, rendered as a row.
 *
 * Pills rather than a `<select>` because the options are few and the current one
 * should be readable without opening anything — on a screen whose whole job is
 * telling you what you are looking at.
 */
export function FilterPills<T extends string>({
  options,
  current,
  onSelect,
  label,
}: {
  options: { value: T; label: string }[];
  current: T;
  onSelect: (value: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} style={s.pills}>
      {options.map((o) => {
        const active = o.value === current;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(o.value)}
            style={{ ...s.pill, ...(active ? s.pillActive : {}) }}
            className="rw-press"
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A menu hanging off a control — the row ellipsis, and the avatar popup.
 *
 * ── The three behaviours a menu needs, and nobody writes ─────────────
 *
 * Outside-click closes, Escape closes, and only one is open at a time. The last
 * one is why this is a shared component: with per-row state, opening a second
 * menu leaves the first hanging, and there is no natural place to notice.
 * A module-level counter gives every instance an id and one shared "which is
 * open", so opening any closes the rest by construction.
 */
let nextMenuId = 0;
const menuListeners = new Set<(open: number | null) => void>();
let openMenu: number | null = null;
function setOpenMenu(id: number | null) {
  openMenu = id;
  for (const l of menuListeners) l(openMenu);
}

export function Menu({
  label,
  trigger,
  align = 'right',
  placement = 'down',
  popupRole = 'menu',
  children,
}: {
  label: string;
  trigger?: ReactNode;
  align?: 'left' | 'right';
  /**
   * Which way it opens (org-admin#9).
   *
   * The avatar sits at the BOTTOM of the sidebar, so a menu opening downwards
   * hangs off the end of the window and the nav has to be scrolled to reach
   * "Sign out" — a control that is always in view becoming one you have to go
   * looking for.
   *
   * A preference rather than an instruction: whichever way it is asked to open,
   * it flips if there is not room. A row menu near the bottom of a long table
   * has the same problem and nobody would think to pass a prop for it.
   */
  placement?: 'down' | 'up';
  /**
   * What the panel IS, for assistive technology (org-admin#56).
   *
   * Everything else here — the portal, the flip, following the trigger on
   * scroll, outside-click, Escape, the single-open registry — is popover
   * plumbing that has nothing to do with menus. The only genuinely menu-shaped
   * things in this component are two ARIA attributes, so an information bubble
   * reusing the rest was a choice between one prop and a second copy of the
   * hundred lines above. Duplicating them is how two popovers come to disagree
   * about what Escape does.
   *
   * `role="menu"` is a lie for prose, not a cosmetic mismatch: it promises
   * `menuitem` children and arrow-key navigation, so a screen reader announces
   * an explanatory paragraph as an empty menu.
   */
  popupRole?: 'menu' | 'dialog';
  children: (close: () => void) => ReactNode;
}) {
  const idRef = useRef<number>(0);
  if (idRef.current === 0) idRef.current = ++nextMenuId;
  const id = idRef.current;

  const [open, setOpen] = useState(false);
  const [actual, setActual] = useState<'down' | 'up'>(placement);
  /**
   * Where the trigger is on screen, in viewport coordinates (#11).
   *
   * The panel is rendered into `document.body` rather than beside the trigger,
   * so it has no positioned ancestor to hang off and needs measurements instead.
   * Null until the first measurement, which is also what stops a panel being
   * painted at 0,0 for one frame on open.
   */
  const [rect, setRect] = useState<{ top: number; bottom: number; left: number; right: number } | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const listener = (current: number | null) => setOpen(current === id);
    menuListeners.add(listener);
    return () => {
      menuListeners.delete(listener);
      // Closing on unmount, or a menu whose row was filtered away stays
      // "open" and blocks the next one from opening.
      if (openMenu === id) setOpenMenu(null);
    };
  }, [id]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      /**
       * The PANEL is checked too, and forgetting it is how this breaks (#11).
       *
       * The panel now lives in `document.body`, so it is no longer inside
       * `wrap` — and this handler runs on `mousedown`, before the click. Without
       * the second test, pressing a menu item counts as an outside click, the
       * panel unmounts under the cursor, and the `click` never lands on
       * anything. The menu would look like it closed without doing the thing.
       */
      if (!wrap.current?.contains(target) && !panel.current?.contains(target)) setOpenMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /**
   * Flip if the preferred direction does not fit.
   *
   * Measured after the panel is in the DOM but before the browser paints, so a
   * menu never appears in one place and jumps to another. `useLayoutEffect` is
   * the whole reason this is not visible.
   */
  /**
   * Follow the trigger while the menu is open.
   *
   * `capture: true` on scroll is the part that matters: scroll events from a
   * scrolling ANCESTOR — the table's own `overflow-x` wrapper, or the page —
   * do not bubble, so a listener without capture never hears them and the
   * panel is left behind while the row moves out from under it.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const r = wrap.current?.getBoundingClientRect();
      if (r) setRect({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !panel.current || !wrap.current) return;
    const trigger = wrap.current.getBoundingClientRect();
    const height = panel.current.offsetHeight;
    const GAP = 8;
    const fitsBelow = trigger.bottom + height + GAP <= window.innerHeight;
    const fitsAbove = trigger.top - height - GAP >= 0;
    // Only override the preference when it genuinely does not fit AND the other
    // way does. With no room either way, the preference wins and it scrolls.
    if (placement === 'down' && !fitsBelow && fitsAbove) setActual('up');
    else if (placement === 'up' && !fitsAbove && fitsBelow) setActual('down');
    else setActual(placement);
  }, [open, placement]);

  return (
    <div ref={wrap} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-haspopup={popupRole}
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpenMenu(open ? null : id)}
        style={
          trigger
            ? // A dialog bubble's trigger is a small inline icon; a menu's fills
              // its block (the sidebar avatar row). Only the menu wants stretching.
              popupRole === 'dialog'
              ? { ...s.plainTrigger, width: 'auto', display: 'inline-flex' }
              : s.plainTrigger
            : s.ellipsis
        }
      >
        {trigger ?? '⋯'}
      </button>
      {/**
        * PORTALLED TO THE BODY, which is the fix for #11 rather than a
        * refactor.
        *
        * The panel used to be an absolutely-positioned sibling of the trigger,
        * so it was clipped by every scrolling or hidden ancestor between here
        * and the root. On a table there are two: the `overflow-x: auto` wrapper
        * around `<table>` — CSS cannot give one axis `auto` and leave the other
        * `visible`, so asking for horizontal scrolling clips vertically as a
        * side effect — and `Panel`'s `overflow: hidden`, which is load-bearing
        * because it is what trims the table's square corners to the panel's
        * radius. Neither can simply be removed, so the panel leaves instead.
        *
        * Positioned `fixed` from the measured trigger rect. `right` is
        * expressed as a distance from the viewport edge rather than a computed
        * `left`, so alignment never needs the panel's own width — which is not
        * known until after it has been painted somewhere.
        */}
      {open && rect
        ? createPortal(
            <div
              ref={panel}
              role={popupRole}
              // A dialog is not in the page's reading order the way a menu is,
              // so it has to name itself; the trigger's label is the name.
              aria-label={popupRole === 'dialog' ? label : undefined}
              style={{
                ...s.menu,
                // Prose needs a measure and real padding. `s.menu`'s 4px is
                // there so menu items can own their own hit area to the edge.
                ...(popupRole === 'dialog' ? s.bubble : {}),
                ...(align === 'left'
                  ? { left: rect.left }
                  : {
                      right: Math.max(0, window.innerWidth - rect.right),
                      /**
                       * A right-aligned panel can only be as wide as the space
                       * to the LEFT of where it is pinned (org-admin#56).
                       *
                       * `right` fixes the right edge to the trigger's, so width
                       * decides where the left edge lands — and a panel wider
                       * than `rect.right` puts it off the side of the window.
                       * Found on the info bubble at 375px: 343px of panel
                       * pinned 340px from the left edge, so three pixels of
                       * border and padding were clipped.
                       *
                       * It does NOT show up as a horizontal scrollbar, because
                       * the panel is `position: fixed` and fixed overflow does
                       * not extend the scrollable area. Nothing about the page
                       * looks wrong; the panel is just quietly cut off.
                       *
                       * Never below 160px — narrower than that the content is
                       * unreadable anyway, and a panel that shrinks to nothing
                       * near the left edge is a worse failure than one that
                       * overhangs.
                       */
                      maxWidth: Math.max(160, rect.right - 8),
                    }),
                ...(actual === 'up'
                  ? { bottom: Math.max(0, window.innerHeight - rect.top + 4) }
                  : { top: rect.bottom + 4 }),
              }}
            >
              {children(() => setOpenMenu(null))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * An `i` that reveals an explanation, for the paragraph that is only worth
 * reading once (org-admin#56).
 *
 * ── Why this exists rather than a `title` attribute ──────────────────
 *
 * The portal's existing idiom for a hint is `title=""`, and it is right for
 * what it is used for — a few words qualifying one table cell. It is the wrong
 * tool the moment the text is a paragraph somebody may actually need: `title`
 * is unreachable by keyboard, invisible on touch, truncated by some browsers,
 * and delayed behind a hover the reader has to already suspect is there.
 *
 * ── And why the standing paragraph had to go ─────────────────────────
 *
 * This replaces a permanent block of explanation on System Health. Text that is
 * on screen every single visit stops being read after the second one, so the
 * page was spending its most prominent space on something nobody was taking in
 * — and that space is needed on the visit where something IS wrong. Explanation
 * on demand; the alert keeps the room.
 *
 * It lives HERE rather than in ui.tsx, next to the popover it is built on:
 * ui.tsx importing `Menu` would have made ui and Screen import each other, and
 * one cycle in this codebase (Screen needs App's HeaderSlot) is enough.
 */
export function InfoBubble({
  label,
  children,
  align = 'right',
}: {
  label: string;
  children: ReactNode;
  /** `left` opens it rightward, for a bubble beside a form label at the left edge. */
  align?: 'left' | 'right';
}) {
  return (
    <Menu
      label={label}
      popupRole="dialog"
      align={align}
      trigger={
        <span aria-hidden style={s.infoDot}>
          i
        </span>
      }
    >
      {/* ONE block, because the panel is `display: grid` so menu items can
          own their rows. Unwrapped, every text run, <strong> and <br> in a
          bubble became its own grid row, and "The **role** is…" rendered as
          three lines (Jon, 2026-09-23). */}
      {() => <div>{children}</div>}
    </Menu>
  );
}

export function MenuItem({
  children,
  onClick,
  href,
  external,
  tone,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  /**
   * Renders a real anchor rather than a button.
   *
   * Same reasoning as the nav in App.tsx: an `onClick` that calls
   * `window.open` has none of the browser's own affordances — no middle click,
   * no copy-link, no status bar showing where it goes. An item that goes
   * somewhere should look and behave like a link, because it is one.
   */
  href?: string;
  /**
   * Leaves this app, so it opens in a new tab and says so with an arrow.
   *
   * Two kinds of link land in these menus and they must not look alike. An
   * ACCOUNT SETTINGS item goes to another service and taking the admin's current
   * tab with it would lose whatever they were in the middle of. A "View profile"
   * item goes to a screen in this portal, and opening THAT in a new tab is a
   * portal that scatters tabs every time somebody follows a row — the arrow
   * would also be a promise it is leaving, which it is not.
   */
  external?: boolean;
  tone?: 'danger';
  disabled?: boolean;
}) {
  const style = {
    ...s.menuItem,
    color: tone === 'danger' ? C.dangerText : C.text,
    opacity: disabled ? 0.45 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };

  if (href) {
    return (
      <a
        role="menuitem"
        href={href}
        target={external ? '_blank' : undefined}
        // `noopener` is the one that matters — without it the opened page gets a
        // handle on this one through `window.opener` and can navigate it. Only
        // meaningful with a target, and harmless without one.
        rel={external ? 'noopener noreferrer' : undefined}
        onClick={onClick}
        className="rw-navhover"
        style={{ ...style, textDecoration: 'none', display: 'flex', justifyContent: 'space-between', gap: '.5rem' }}
      >
        {children}
        {external ? (
          <span aria-hidden style={{ color: C.muted }}>
            ↗
          </span>
        ) : null}
      </a>
    );
  }

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className="rw-navhover"
      style={style}
    >
      {children}
    </button>
  );
}

/**
 * The FILLED action — the point of whatever it sits on.
 *
 * ── ONE per screen: the action row, and nowhere else (Jon, 2026-08-28) ─
 *
 * An action bar of identically-weighted buttons is one where nothing is the
 * point. So exactly one filled control per screen, in the header's action row:
 * `+ Add user`, `+ Add application`, `+ New agreement`, `+ New group`. Panel
 * adds are the outline `Button`.
 *
 * ── This was widened to one-per-panel, and widened back (same day) ───
 *
 * The argument for widening: a record's panels each own one add — grant an
 * application, assign a role, assign an app role, add to a group — and each is
 * the point of its panel in the way the screen action is the point of its
 * screen. It survived a day. What killed it is what the argument left out: the
 * Access tab has four such panels stacked, so a person scrolling one account met
 * four filled buttons down one screen, and at four the fill has stopped saying
 * "this is the point of the surface" and says only "an add lives here". A
 * treatment that appears on every panel is not emphasis, it is the background.
 *
 * The other half of that argument was that outlined adds "read as secondary to
 * the Remove buttons underneath them", and it does not survive reading the two
 * controls side by side: `Button` and `HoldButton` at rest are the same padding,
 * the same .85rem/600, the same radius, both on a transparent-or-surface ground,
 * differing only in which colour the text and border take. They are peers. The
 * add is also in the panel HEADER while the Removes are at the ends of rows, so
 * the two never sit in the same group and never compete.
 *
 * ── The invariant, which did not change either time ──────────────────
 *
 * ONE filled control per group. Two in the same header is the failure this has
 * always been guarding against; that is why widening looked safe. Per-group is
 * necessary and not sufficient — a page can satisfy it and still be four filled
 * buttons long.
 */
export function PrimaryButton({
  children,
  onClick,
  disabled,
  title,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Why it cannot be pressed. A disabled control with no explanation is
   *  indistinguishable from a broken one — the rule the Access tab's disabled
   *  Remove already follows. See lib/capabilities.tsx `whyDisabled`. */
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="rw-press"
      /**
       * ── DISABLED FADES. IT DOES NOT TURN GREY (Jon, 2026-08-28) ──────
       *
       * This used to swap the fill to `C.muted` while leaving the border on
       * `C.brand`, so a disabled primary was a grey pill inside a coloured ring
       * — a different colour from the button it replaced rather than the same
       * button, unavailable. It was also the ONLY control here that recoloured:
       * `Button`, `HoldButton` and `MenuItem` all keep their colour and drop
       * their opacity, so one disabled control looked unlike every other.
       *
       * `opacity` on the whole element instead, at the same 0.5 the two
       * controls it sits beside use. Recognisably this button, visibly not
       * available.
       *
       * If it ever reads as too faint, the fix is the number — not a grey fill,
       * which is what was just removed.
       */
      style={{
        padding: '.42rem .8rem',
        fontSize: '.85rem',
        fontWeight: 640,
        borderRadius: 6,
        border: `1px solid ${C.brand}`,
        background: C.brand,
        // The INVERSE of the ground, not white. `C.brand` is #2B63E8 in light and
        // #6E98FF in dark, so a literal white gives 5.18:1 in light and 2.76:1
        // in dark -- a primary button that fails AA in half the themes.
        // `C.surface` is white in light and #131A28 in dark: 5.18 and 6.30.
        color: C.surface,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * A filter that is not a search chip, rendered as a removable one anyway.
 *
 * A screen opened pre-filtered, from a profile or from a click on a row, must
 * SAY it is filtered, or an empty list reads as "nothing happened". Shared by
 * Activity and Notifications, which both filter by clicking a cell.
 */
export function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span style={s.filterChip}>
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} style={s.filterChipX}>
        ✕
      </button>
    </span>
  );
}

/** Re-exported rather than redefined, so a screen has one import for its
 *  layout vocabulary. Two copies of a control's styling is one rebrand away from
 *  a form whose inputs do not match each other. */
export { inputStyle };

const s = {
  filterChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    background: C.violetTint,
    color: C.violetText,
    fontSize: '.8rem',
    fontWeight: 600,
    padding: '.2rem .2rem .2rem .55rem',
  },
  filterChipX: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    font: 'inherit',
    fontSize: '.7rem',
    padding: '.1rem .45rem',
    cursor: 'pointer',
    opacity: 0.75,
  },
  /** The two bands, stacked, filling the header. */
  headerBands: { display: 'grid', gap: '.5rem', width: '100%', minWidth: 0 },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap' as const,
    minHeight: 34,
  },
  actionLeft: { display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' as const },
  actionRight: { display: 'flex', alignItems: 'center', gap: '.5rem', marginLeft: 'auto' },
  pills: {
    display: 'inline-flex',
    gap: 2,
    padding: 2,
    background: C.page,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
  },
  pill: {
    padding: '.3rem .65rem',
    fontSize: '.82rem',
    fontWeight: 600,
    borderRadius: 6,
    border: '1px solid transparent',
    background: 'transparent',
    color: C.textMid2,
    cursor: 'pointer',
  },
  pillActive: { background: C.surface, color: C.text, border: `1px solid ${C.border}` },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    border: `1px solid ${C.blueTint}`,
    background: C.blueTint,
    color: C.blueText,
    fontSize: '.8rem',
    fontWeight: 600,
    overflow: 'hidden',
  },
  // A disabled chip stays legible rather than fading out — it is still an
  // instruction to the reader, just not to the server.
  chipOff: {
    background: 'transparent',
    borderColor: C.inputBorder,
    color: C.muted,
    textDecoration: 'line-through',
  },
  chipBody: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    font: 'inherit',
    padding: '.2rem .1rem .2rem .55rem',
    cursor: 'pointer',
  },
  chipX: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    font: 'inherit',
    fontSize: '.7rem',
    padding: '.2rem .5rem .2rem .35rem',
    cursor: 'pointer',
    opacity: 0.75,
  },
  dropped: {
    margin: 0,
    fontSize: '.8rem',
    color: C.warnText,
    background: C.warnBg,
    padding: '.4rem .6rem',
    borderRadius: 6,
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: C.brand,
    font: 'inherit',
    fontSize: '.82rem',
    cursor: 'pointer',
    padding: '.2rem .3rem',
  },
  ellipsis: {
    background: 'none',
    border: `1px solid transparent`,
    borderRadius: 6,
    color: C.textMid2,
    cursor: 'pointer',
    fontSize: '1rem',
    lineHeight: 1,
    padding: '.25rem .45rem',
  },
  plainTrigger: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    textAlign: 'left' as const,
    width: '100%',
  },
  menu: {
    // FIXED, not absolute: the panel is portalled to the body, so it is
    // positioned against the viewport from the trigger's measured rect (#11).
    position: 'fixed' as const,
    // `top` / `bottom` / `left` / `right` are applied where it renders — see
    // the measurement and the flip in `Menu`.
    minWidth: 190,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 9,
    // Near-black at 12% reads as depth on a light page and as nothing at all
    // on a dark one. The variable carries a heavier shadow for dark; see
    // styles.css.
    boxShadow: 'var(--rw-admin-shadow-sm, 0 8px 24px rgba(16, 22, 38, .12))',
    padding: 4,
    // Above the Modal (40), like Select's list (60): a menu opened inside a
    // dialog, such as + Add platform, otherwise opens behind it and looks dead.
    zIndex: 60,
    display: 'grid',
  },
  /** Overrides on `menu` for a prose bubble. A measure of ~34em is the readable
   *  ceiling; below that it tracks the viewport so a narrow window still fits. */
  /** Small, round and quiet. It sits beside a status pill, so it has to be
   *  findable without competing with the thing it annotates. */
  infoDot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: `1px solid ${C.border}`,
    background: C.page,
    color: C.muted,
    fontSize: 11,
    fontWeight: 700,
    fontStyle: 'italic' as const,
    lineHeight: 1,
    fontFamily: 'Georgia, serif',
  },
  bubble: {
    minWidth: 0,
    width: 'min(30rem, calc(100vw - 2rem))',
    padding: '.85rem 1rem',
    fontSize: '.85rem',
    lineHeight: 1.55,
    color: C.textMid,
  },
  menuItem: {
    background: 'none',
    border: 'none',
    textAlign: 'left' as const,
    padding: '.45rem .6rem',
    borderRadius: 6,
    font: 'inherit',
    fontSize: '.85rem',
  },
};
